import { makeStyles } from 'tss-react/mui';
import { P2GBox, useTheme } from '@parcel2go/core-ui';
import { Restrictions } from '~/lib/types/basket-types';
import { ContentBlock } from '~/components/core/content-block/content-block';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { memo } from 'react';

interface Props {
  restrictions: Restrictions;
}

interface SizeProps {
  title: 'Max Length' | 'Max Width' | 'Max Height' | string;
  value: number;
}

const useStyles = makeStyles()((theme) => ({
  parcelDimensionContainer: {
    paddingTop: theme.spacing(5),
    position: 'relative'
  },
  length: {
    position: 'absolute',
    bottom: theme.spacing(9),
    left: '100px'
  },
  width: {
    position: 'absolute',
    bottom: theme.spacing(-1),
    left: '100px'
  },
  height: {
    position: 'absolute',
    bottom: theme.spacing(4),
    left: '155px'
  }
}));

const Size = memo(({ title, value }: SizeProps) => {
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);
  const translationKey = `PARCEL_DIMENSION_${title.trim().toUpperCase().replace(/\s+/g, '_')}`;

  return (
    <small>
      <Trans
        i18nKey={translationKey}
        t={t}
        values={{ title, size: value }}
        defaults="{{title}}: <strong>{{size}} cm</strong>"
      />
    </small>
  );
});

export const WeightAndSize = ({ restrictions }: Props): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);

  const hasDimensions = restrictions.maxLength || restrictions.maxWidth || restrictions.maxHeight;

  let parcelDimension = '';
  if (hasDimensions) {
    const dimensions = [restrictions.maxLength, restrictions.maxWidth, restrictions.maxHeight].filter(Boolean);

    parcelDimension = t('PARCEL_DIMENSION', {
      defaultValue: 'Max size - {{dimension}} cm',
      dimension: dimensions.join(' x ')
    });
  }

  return (
    <ContentBlock title={t('DIMENSION_RESTRICTIONS_SUBTITLE', { defaultValue: 'Size & weight restrictions' })}>
      <P2GBox>
        {t('WEIGHT_DIMENSION_INFO', {
          defaultValue: 'Send up to {{maxWeight}} kg per parcel. {{parcelDimension}}',
          maxWeight: restrictions.maxWeight,
          parcelDimension
        })}
      </P2GBox>

      <P2GBox spacing={0} classNames={classes.parcelDimensionContainer}>
        <ParcelDimensionIcon />
        {restrictions.maxLength && (
          <P2GBox classNames={classes.length}>
            <Size title="Max Length" value={restrictions.maxLength} />
          </P2GBox>
        )}
        {restrictions.maxHeight && (
          <P2GBox classNames={classes.height}>
            <Size title="Max Height" value={restrictions.maxHeight} />
          </P2GBox>
        )}
        {restrictions.maxWidth && (
          <P2GBox classNames={classes.width}>
            <Size title="Max Width" value={restrictions.maxWidth} />
          </P2GBox>
        )}
      </P2GBox>
    </ContentBlock>
  );
};

export const ParcelDimensionIcon = (): JSX.Element => {
  const theme = useTheme();
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="144" height="77" fill="none">
      <path
        fill={theme.palette.primary.dark}
        fillRule="evenodd"
        d="M144 24.586 57.284.956 0 19.762v18.97l77.858 37.58L144 46.89V24.586ZM35.237 10.299l22.1-7.255 82.582 22.503-29.407 12.21-75.275-27.458Zm-3.071 1.008-28.31 9.294 73.533 30.908 30.394-12.62-75.617-27.582Zm46.22 41.953L142 26.85v18.74l-63.614 28.3V53.26ZM2 21.99v15.487L76.386 73.38V53.257L2 21.99Z"
        clipRule="evenodd"
      />
    </svg>
  );
};
